<script setup lang="ts">
import { computed, ref } from 'vue'
import * as formatNumber from '@/helpers/numberFormatters'
import type { LoginSectionProps, SmartbookGrantContext } from '../useLoginSection'

const props = defineProps<LoginSectionProps & { context: SmartbookGrantContext }>()

const paymentModal = ref<{ open: (payload: Record<string, unknown>) => void } | null>(null)

const selectedStudentsNames = computed(() => {
  return (props.sharedData.smartbookStudents || [])
    .map(({ first_name, last_name }) => `${last_name} ${first_name}`)
    .join(', ')
})

const smartbookInfo = computed(() => {
  const base = props.context.smartbook
  const tariff = props.query.get('smartbook_tariff') || 'b'
  const price = { b: base?.basic_price, e: base?.extended_price }[tariff] || 0
  const tariffString = { b: 'Стандартная версия', e: 'Расширенная версия' }[tariff] || ''
  const accessMonths = { b: '18 месяцев', e: '24 месяца' }[tariff] || ''
  return {
    price,
    tariffString,
    accessMonths,
    course_name: base?.course_name,
    course_id: base?.course_id
  }
})

const purchaseInfo = computed(() => {
  const quantity = props.sharedData.smartbookStudents ? props.sharedData.smartbookStudents.length : 1
  return { amount: smartbookInfo.value.price * quantity, quantity }
})

function formatPrice(val?: number) {
  if (!val) return '—'
  return formatNumber.price.rub(val)
}

function doPurchase() {
  const { quantity, amount } = purchaseInfo.value
  const studentIds = (props.sharedData.smartbookStudents || []).map((item: { id: number }) => item.id).join(',')

  paymentModal.value?.open({
    order: {
      name: `«Умный учебник» ${smartbookInfo.value.course_name} (${smartbookInfo.value.tariffString}, ${quantity} шт.)`,
      price: amount
    },
    query: {
      student_ids: studentIds,
      course_id: smartbookInfo.value.course_id,
      tariff: props.query.get('smartbook_tariff') || 'b'
    }
  })
}
</script>

<template>
  <div class="login-smartbook-purchase">
    <v-payment-modal ref="paymentModal" url="/parent/request_purchase_smartbook" />

    <h2 class="text-h3">Оплата</h2>

    <ul class="login-smartbook-purchase__info-grid mt-4 mb-3">
      <span class="gray-text">Ребенок:</span>
      <li v-text="selectedStudentsNames" />

      <span class="gray-text">Курс:</span>
      <li v-text="smartbookInfo.course_name" />
    </ul>

    <div class="flex-adaptive text-body-bold full-width">
      <span class="flex-spacer">
        «Умный учебник» {{ smartbookInfo.course_name }} ({{ smartbookInfo.tariffString }})
      </span>

      <div class="ma-half" />

      <span>{{ formatPrice(purchaseInfo.amount) }}</span>
    </div>

    <v-button class="mt-3" mode="primary filled block" @click="doPurchase"> Оплатить онлайн </v-button>
  </div>
</template>

<style lang="scss">
.login-smartbook-purchase {
  display: flex;
  flex-direction: column;
  align-items: center;

  @include desktop {
    width: 400px !important;
    margin: 0 auto;
  }

  &__info-grid {
    width: 100%;
    display: grid;
    gap: 8px;

    @include desktop {
      grid-template-columns: 1fr auto;
    }
  }
}
</style>
